import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as api from "./api";
import type { AiSheetDimension, AiSheetFilters } from "./types";

const qk = {
  sheets: ["pp", "ai-sheets"] as const,
  data: (id: string) => ["pp", "ai-sheet-data", id] as const,
  options: ["pp", "ai-sheet-options"] as const,
  spend: ["pp", "ai-sheet-spend"] as const,
  candidates: (id: string, s: string) => ["pp", "ai-sheet-candidates", id, s] as const,
};

export const useSheets = () => useQuery({ queryKey: qk.sheets, queryFn: api.listSheets });

export const useSheetData = (id: string | null) =>
  useQuery({
    queryKey: qk.data(id ?? ""),
    queryFn: () => api.getData(id!),
    enabled: !!id,
    placeholderData: (prev) => prev,
  });

export const useSheetOptions = () =>
  useQuery({ queryKey: qk.options, queryFn: api.getOptions, staleTime: 5 * 60 * 1000 });

export const useSpend = () =>
  useQuery({ queryKey: qk.spend, queryFn: api.getSpend, refetchInterval: 60_000 });

export const useSetBudget = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (budget: string) => api.setBudget(budget),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.spend }),
  });
};

export const useShareEligible = (id: string | null, enabled: boolean) =>
  useQuery({
    queryKey: ["pp", "ai-sheet-share-eligible", id ?? ""],
    queryFn: () => api.shareEligible(id!),
    enabled: enabled && !!id,
  });

export const useRowCandidates = (id: string | null, search: string, enabled: boolean) =>
  useQuery({
    queryKey: qk.candidates(id ?? "", search),
    queryFn: () => api.rowsCandidates(id!, search),
    enabled: enabled && !!id,
    placeholderData: (prev) => prev,
  });

export const useSheetMutations = (sheetId: string | null) => {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: qk.sheets });
    if (sheetId) void qc.invalidateQueries({ queryKey: qk.data(sheetId) });
    void qc.invalidateQueries({ queryKey: qk.spend });
  };
  return {
    invalidate,
    createSheet: useMutation({
      mutationFn: (v: { name: string; row_dimension: AiSheetDimension; filters: AiSheetFilters }) =>
        api.createSheet(v),
      onSuccess: invalidate,
    }),
    patchSheet: useMutation({
      mutationFn: (v: { id: string; patch: Parameters<typeof api.patchSheet>[1] }) =>
        api.patchSheet(v.id, v.patch),
      onSuccess: invalidate,
    }),
    deleteSheet: useMutation({
      mutationFn: (id: string) => api.deleteSheet(id),
      onSuccess: invalidate,
    }),
    rowsSync: useMutation({
      mutationFn: (v: { apply: boolean }) => api.rowsSync(sheetId!, v.apply),
      onSuccess: (_d, v) => {
        if (v.apply) invalidate();
      },
    }),
    rowsAdd: useMutation({
      mutationFn: (entityId: string) => api.rowsAdd(sheetId!, entityId),
      onSuccess: invalidate,
    }),
    rowsRemove: useMutation({
      mutationFn: (rowId: string) => api.rowsRemove(sheetId!, rowId),
      onSuccess: invalidate,
    }),
    addSystemColumn: useMutation({
      mutationFn: (field: string) => api.addSystemColumn(sheetId!, field),
      onSuccess: invalidate,
    }),
    addManualColumn: useMutation({
      mutationFn: (v: { label: string; fmt: string }) => api.addManualColumn(sheetId!, v),
      onSuccess: invalidate,
    }),
    setCell: useMutation({
      mutationFn: (v: { row_id: string; column_id: string; value: string | number | null }) =>
        api.setCellValue(sheetId!, v),
      onSuccess: invalidate,
    }),
    planColumn: useMutation({
      mutationFn: (v: { label: string; description: string; replace_column_id?: string }) =>
        api.planColumn(sheetId!, v),
    }),
    chatColumn: useMutation({
      mutationFn: (v: { colId: string; message: string }) =>
        api.chatColumn(sheetId!, v.colId, v.message),
      // The user's message is stored server-side straight away — refetch so the
      // thread shows it while ARON is still thinking.
      onSuccess: invalidate,
    }),
    applyColumn: useMutation({
      mutationFn: (v: string | { colId: string; force?: boolean }) =>
        typeof v === "string"
          ? api.applyColumn(sheetId!, v)
          : api.applyColumn(sheetId!, v.colId, v.force),
      onSuccess: invalidate,
    }),
    discardColumn: useMutation({
      mutationFn: (colId: string) => api.discardColumn(sheetId!, colId),
      onSuccess: invalidate,
    }),
    deleteColumn: useMutation({
      mutationFn: (colId: string) => api.deleteColumn(sheetId!, colId),
      onSuccess: invalidate,
    }),
    refresh: useMutation({
      mutationFn: (v: Parameters<typeof api.refreshSheet>[1]) => api.refreshSheet(sheetId!, v),
      onSuccess: () => {
        // PENDING flips are visible immediately; values land when the task ends.
        if (sheetId) void qc.invalidateQueries({ queryKey: qk.data(sheetId) });
      },
    }),
    copySheet: useMutation({
      mutationFn: (id: string) => api.copySheet(id),
      onSuccess: invalidate,
    }),
    share: useMutation({
      mutationFn: (v: { userId: string; on: boolean }) =>
        v.on ? api.shareSheet(sheetId!, v.userId) : api.unshareSheet(sheetId!, v.userId),
      onSuccess: () => {
        invalidate();
        void qc.invalidateQueries({ queryKey: ["pp", "ai-sheet-share-eligible", sheetId ?? ""] });
      },
    }),
    moveRow: useMutation({
      mutationFn: (v: { rowId: string; direction: "up" | "down" }) =>
        api.moveRow(sheetId!, v.rowId, v.direction),
      onSuccess: invalidate,
    }),
    moveColumn: useMutation({
      mutationFn: (v: { columnId: string; direction: "left" | "right" }) =>
        api.moveColumn(sheetId!, v.columnId, v.direction),
      onSuccess: invalidate,
    }),
  };
};
